import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator } from "redux/baseQuery";
import type {
  CreateUserRequest,
  GetDetailOrganizationResponse,
  GetDetailOrganizationRequest,
  GetOrganizationRequest,
  GetOrganizationResponse,
  GetOrganizationsRequest,
  GetOrganizationsResponse,
  GetProfileResponse,
  GetUserRequest,
  GetUsersByOrganisationRequest,
  GetUsersByOrganisationResponse,
  GetUsersRequest,
  GetUsersResponse,
  UpdateOrganisationRequest,
  UpdateProfileRequest,
  UpdateOrganisationUserRequest,
  CreateOrganisationUserRequest,
  RemoveOrganisationUserRequest,
  RemoveAddressRequest,
  RemoveContactRequest,
  UpdateMerchantUserEmploymentResponse,
  CreateMerchantUserEmploymentRequest,
  UpdateKYCRequest,
  EmploymentDetail,
  CreateOrganisationRequest,
  UpdateWebhookRequest,
} from "redux/services/membership/types";
import env from "env";
import dayjs from "dayjs";
const { membershipService } = env.api.baseUrl;

const baseQuery = fetchBaseQuery({
  baseUrl: membershipService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const membershipApi = createApi({
  reducerPath: "membershipApi",
  tagTypes: ["User", "organisations"],
  baseQuery: baseQueryCreator(baseQuery),
  endpoints: (builder) => ({
    getProfile: builder.query<GetProfileResponse, void>({
      query: () => "users/me",
    }),
    getOrganization: builder.query<
      GetOrganizationResponse,
      GetOrganizationRequest
    >({
      query: ({ orgId, ...params }) => ({
        url: `organisations/${orgId}`,
        params: {
          ...params,
        },
      }),
    }),
    updateProfile: builder.mutation<GetProfileResponse, UpdateProfileRequest>({
      query: ({ userId, ...patch }) => ({
        url: `users/${userId}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    updateKYC: builder.mutation<
      UpdateMerchantUserEmploymentResponse,
      UpdateKYCRequest
    >({
      query: ({ userId, ...patch }) => ({
        url: `users/${userId}/kyc-details`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: () => [{ type: "User" }],
    }),
    createMerchantUserEmployment: builder.mutation<
      UpdateMerchantUserEmploymentResponse,
      CreateMerchantUserEmploymentRequest
    >({
      query: ({ userId, ...patch }) => ({
        url: `users/${userId}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    updateMerchantUserEmployment: builder.mutation<
      UpdateMerchantUserEmploymentResponse,
      EmploymentDetail
    >({
      query: ({ id, ...patch }) => ({
        url: `/employments/${id}`,
        method: "put",
        body: {
          ...patch,
          startDate: patch.startDate
            ? dayjs(patch.startDate).format("YYYY-MM-DD")
            : null,
          endDate: patch.endDate
            ? dayjs(patch.endDate).format("YYYY-MM-DD")
            : null,
        },
      }),
    }),
    getUsers: builder.query<
      GetUsersResponse,
      GetUsersRequest & {
        orgId?: string;
      }
    >({
      query: ({ orgToken, ...params }) => {
        if (orgToken === "") {
          return {
            url: "/admin/users",
            params: {
              listOrders: "firstName-ASC",
              ...params,
            },
          };
        }
        return {
          url: "/admin/users",
          params: {
            listOrders: "firstName-ASC",
            ...params,
          },
          headers: {
            "org-token": orgToken,
          },
        };
      },
      providesTags: () => [{ type: "User" }],
    }),
    getOrganizations: builder.query<
      GetOrganizationsResponse,
      GetOrganizationsRequest
    >({
      query: ({ orgToken, parentOrgId, ...restParams }) => {
        if (orgToken === "") {
          return {
            url: "/admin/organisations",
            params: {
              ...restParams,
            },
          };
        }
        return {
          url: "/admin/organisations",
          params: {
            ...restParams,
            parentOrgId,
          },
          headers: {
            "org-token": orgToken,
          },
        };
      },
    }),
    getDetailOrganization: builder.query<
      GetDetailOrganizationResponse,
      GetDetailOrganizationRequest
    >({
      query: (params) => ({
        url: `/admin/organisations/${params.orgId}`,
      }),
      providesTags: () => [{ type: "organisations" }],
    }),
    getUsersByOrganisation: builder.query<
      GetUsersByOrganisationResponse,
      GetUsersByOrganisationRequest
    >({
      query: ({ orgId, ...params }) => {
        return {
          url: `/organisations/${orgId}/users`,
          params: {
            ...params,
          },
        };
      },
    }),
    getUser: builder.mutation<GetProfileResponse, GetUserRequest>({
      query: (params) => ({
        url: `users/${params.userId}`,
      }),
    }),
    getUserV2: builder.query<GetProfileResponse, GetUserRequest>({
      query: (params) => ({
        url: `users/${params.userId}`,
      }),
      providesTags: () => [{ type: "User" }],
    }),
    removeUserOrganisation: builder.mutation<{}, RemoveOrganisationUserRequest>(
      {
        query: ({ orgId, id }) => ({
          url: `organisations/${orgId}/users/${id}`,
          method: "DELETE",
        }),
      }
    ),
    removeAddress: builder.mutation<{}, RemoveAddressRequest>({
      query: ({ id }) => ({
        url: `addresses/${id}`,
        method: "DELETE",
      }),
    }),
    removeContact: builder.mutation<{}, RemoveContactRequest>({
      query: ({ id }) => ({
        url: `contacts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "User" }, { type: "organisations" }],
    }),
    updateUserOrganisation: builder.mutation<{}, UpdateOrganisationUserRequest>(
      {
        query: ({ userId, orgId, ...patch }) => ({
          url: `organisations/${orgId}?userId=${userId}`,
          method: "PATCH",
          body: patch,
        }),
      }
    ),
    createUserOrganisation: builder.mutation<{}, CreateOrganisationUserRequest>(
      {
        query: ({ orgId, ...body }) => ({
          url: `organisations/${orgId}/users`,
          method: "POST",
          body: {
            ...body,
            roleName: "OrganisationUser",
          },
        }),
      }
    ),
    createOrganisation: builder.mutation<{}, CreateOrganisationRequest>({
      query: (payload) => ({
        url: `/admin/organisations`,
        method: "POST",
        body: {
          ...payload,
        },
      }),
    }),
    updateWebhook: builder.mutation<{}, UpdateWebhookRequest>({
      query: ({ orgId, ...patch }) => ({
        url: `/admin/organisations/${orgId}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    updateOrganisation: builder.mutation<{}, UpdateOrganisationRequest>({
      query: ({ orgId, ...patch }) => ({
        url: `organisations/${orgId}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: () => [{ type: "organisations" }],
    }),
    createUser: builder.mutation<{}, CreateUserRequest>({
      query: ({ ...body }) => ({
        url: `admin/users`,
        method: "POST",
        body: {
          ...body,
          appId: env.app.appName,
          accountType: "FRONT-END",
          listCustomFields: [
            {
              userId: body.userId,
              customKey: "emailVerified",
              customValue: "true",
            },
          ],
        },
      }),
    }),
    deleteEmployment: builder.mutation<{}, { id: string }>({
      query: ({ id }) => ({
        url: `employments/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetProfileQuery,
  useGetOrganizationQuery,
  useUpdateProfileMutation,
  useGetUsersQuery,
  useGetUserMutation,
  useGetUserV2Query,
  useUpdateUserOrganisationMutation,
  useGetUsersByOrganisationQuery,
  useCreateUserOrganisationMutation,
  useRemoveUserOrganisationMutation,
  useGetOrganizationsQuery,
  useGetDetailOrganizationQuery,
  useCreateUserMutation,
  useUpdateOrganisationMutation,
  useRemoveAddressMutation,
  useRemoveContactMutation,
  useCreateMerchantUserEmploymentMutation,
  useUpdateMerchantUserEmploymentMutation,
  useDeleteEmploymentMutation,
  useUpdateKYCMutation,
  useCreateOrganisationMutation,
  useUpdateWebhookMutation,
} = membershipApi;

export const {
  useLazyQuery: useGetProfileLazyQuery,
  useQueryState: useGetProfileQueryState,
} = membershipApi.endpoints.getProfile;

export const {
  useLazyQuery: useGetOrganizationLazyQuery,
  useQueryState: useGetOrganizationQueryState,
} = membershipApi.endpoints.getOrganization;
